// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React from "react"; // Valtech Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import siteMessage from "@insite/client-framework/SiteMessage";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import getVerticalAlignmentField, {
    verticalAlignmentOptions,
} from "@store/Framework/Common/Fields/Toolkit/verticalAlignment";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import Carousel, {
    CarouselPresentationProps,
    CarouselSlide,
    getItemsPerSlideFromFieldValue,
} from "@store/Overrides/Components/Carousel";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import withHookProps, { HookPropsInjector } from "@toolkit/Framework/Common/Utilities/withHookProps";
import { focusUpdateStyles } from "@toolkit/Themes/common/mixins/common";
import React, { createRef } from "react";

const imagesGap = pxToRem(30);

const hookPropsInjector: HookPropsInjector<Props> = ({ brandsState }) => ({
    persistentDataView: usePersistentDataView(brandsState),
});
/* Valtech Custom Code - End */

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    // Valtech Custom Code
    brandsHorizontalAlignment = "brandsHorizontalAlignment",
    brandsVerticalAlignment = "brandsVerticalAlignment",
    rowsAlignment = "rowsAlignment",
    showCarousel = "showCarousel",
    showDots = "showDots",
    titleAlignment = "titleAlignment",
}

/* Valtech Custom Code - Start */
const brandsHorizontalAlignmentField = getTextAlignmentField(fields.brandsHorizontalAlignment);
const brandsPerRowField = getNumberTextField(fields.maximumNumberOfBrandsPerRow, { integer: true, min: 1 });
const brandsVerticalAlignmentField = getVerticalAlignmentField(fields.brandsVerticalAlignment);
const rowsAlignmentField = getTextAlignmentField(fields.rowsAlignment);
const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);
/* Valtech Custom Code - End */

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        // Valtech Custom Code
        [fields.brandsHorizontalAlignment]: string;
        [fields.brandsVerticalAlignment]: string;
        [fields.rowsAlignment]: string;
        [fields.showCarousel]: boolean;
        [fields.showDots]: boolean;
        [fields.titleAlignment]: string;
    };
    // Valtech Custom Code
    persistentDataView: ReturnType<typeof getBrandsDataView>;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    // Valtech Custom Code
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    loadingOverlay?: LoadingOverlayProps;
    flexWrapper?: FlexWrapperPresentationProps;
    carousel?: CarouselPresentationProps;
}

export const listStyles: BrandListStyles = {
    titleText: { variant: "h2" },
    noResultsText: typographyGetNoResultsProps(),
    container: widgetWrapperStyles,
    carousel: {
        arrowsGap: pxToRem(20),
        extendedStyles: {
            carouselSlide: {
                css: css`
                    display: flex;
                    flex-direction: column;
                `,
            },
        },
        itemsGap: imagesGap,
    },
    flexWrapper: {
        gap: imagesGap,
        extendedStyles: {
            content: { forwardAs: "ul" },
            item: {
                forwardAs: "li",
                css: css`
                    z-index: 0;
                `,
            },
        },
    },
    link: {
        mergeCss: true,
        css: css`
            ${focusUpdateStyles(true)}
        `,
    },
    image: {
        mergeCss: true,
        css: css`
            z-index: -1;
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    /* Valtech Custom Code - Start */
    componentDidUpdate(prevProps: Props) {
        const {
            brandGalleryParameter,
            brandsState: { isLoading },
            loadBrandGallery,
        } = this.props;

        if (
            !isLoading &&
            (prevProps.brandGalleryParameter.brandIds.toString() !== brandGalleryParameter.brandIds.toString() ||
                prevProps.brandGalleryParameter.randomize !== brandGalleryParameter.randomize ||
                prevProps.brandGalleryParameter.pageSize !== brandGalleryParameter.pageSize)
        ) {
            loadBrandGallery(brandGalleryParameter);
        }
    }
    /* Valtech Custom Code - End */

    render() {
        const {
            // Valtech Removed - brandsState
            fields,
            // Valtech Custom Code
            fields: {
                brandsHorizontalAlignment: baseBrandsHorizontalAlignment,
                brandsVerticalAlignment: baseBrandsVerticalAlignment,
                maximumBrandImageHeight,
                maximumNumberOfBrandsPerRow: baseMaximumNumberOfBrandsPerRow,
                rowsAlignment: baseRowsAlignment,
                showCarousel,
                showDots,
                title,
                titleAlignment: baseTitleAlignment,
            },
            id,
            persistentDataView: { value: brandList },
        } = this.props;

        /* Valtech Removed - return null */

        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: maximumBrandImageHeight ? pxToRem(maximumBrandImageHeight) : "none", // Valtech Custom Code
            },
        };

        const focusFunction = () => {
            const elementToFocus = document.getElementById("collapseButton");
            if (elementToFocus) {
                elementToFocus.focus();
            } else {
                document.getElementById("expandButton")?.focus();
            }
        };

        /* Valtech Custom Code - Start */
        const afterGallery = createRef<HTMLSpanElement>();
        const brandsHorizontalAlignment = brandsHorizontalAlignmentField.parseToFlex(baseBrandsHorizontalAlignment);
        const brandsPerRow = getItemsPerSlideFromFieldValue(baseMaximumNumberOfBrandsPerRow, brandsPerRowField.parse);
        const brandsVerticalAlignment = brandsVerticalAlignmentField.parseToFlex(baseBrandsVerticalAlignment);
        const hasBrands = Boolean(brandList?.length);
        const rowsAlignment = rowsAlignmentField.parseToFlex(baseRowsAlignment);
        const titleAlignment = titleAlignmentField.parse(baseTitleAlignment);
        const titleId = `brand-gallery-title-${id}`;

        const slides: CarouselSlide[] =
            brandList?.map(brand => ({
                id: brand.id,
                renderComponent: () => (
                    <Clickable {...styles.link} href={brand.detailPagePath} key={brand.id}>
                        <LazyImage
                            {...styles.image}
                            altText={translate("Go to {0} brand page", brand.logoAltText || brand.name)}
                            imgProps={imgProps}
                            src={brand.logoSmallImagePath}
                        />
                    </Clickable>
                ),
            })) || [];

        const titleStyles = mergeProps<BrandListStyles["titleText"]>(styles.titleText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });

        const noResultsTextStyles = mergeProps<BrandListStyles["noResultsText"]>(styles.noResultsText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });

        const carouselStyles = mergeProps<BrandListStyles["carousel"]>(styles.carousel, {
            extendedStyles: {
                carouselSlide: {
                    css: css`
                        ${cssProp("align-items", brandsHorizontalAlignment)}
                    `,
                },
            },
        });

        const flexWrapperStyles = mergeProps<BrandListStyles["flexWrapper"]>(styles.flexWrapper, {
            extendedStyles: {
                content: {
                    css: css`
                        ${cssProp("align-items", brandsVerticalAlignment)}
                        ${cssProp("justify-content", rowsAlignment)}
                    `,
                },
                item: {
                    css: css`
                        ${cssProp("justify-content", brandsHorizontalAlignment)}
                    `,
                },
            },
        });

        return (
            <>
                {title ? (
                    <Typography {...titleStyles} data-test-selector="brandGalleryTitle" id={titleId}>
                        {title}
                    </Typography>
                ) : (
                    <VisuallyHidden id={titleId}>{translate("Explore available brands")}</VisuallyHidden>
                )}
                <StyledWrapper {...styles.container} data-test-selector="brandGallery">
                    {hasBrands && (
                        <SkipNav
                            extendedStyles={styles.skipToContent}
                            text={translate("List of {0} items, skip list?", `${brandList?.length}`)}
                            destination={afterGallery}
                        />
                    )}
                    <DataViewLoadingOverlay
                        {...styles.loadingOverlay}
                        dataView={this.props.persistentDataView}
                        minHeight={maximumBrandImageHeight || 100}
                    >
                        {!hasBrands ? (
                            <StyledWrapper {...styles.noResultsContainer}>
                                <Typography {...noResultsTextStyles}>{siteMessage("Brands_Not_Found")}</Typography>
                            </StyledWrapper>
                        ) : showCarousel ? (
                            <Carousel
                                {...carouselStyles}
                                id={`brand-gallery-${id}`}
                                itemsAlign={brandsVerticalAlignment}
                                itemsJustify={rowsAlignment}
                                itemsPerSlide={brandsPerRow}
                                showDots={showDots}
                                slides={slides}
                                titleId={title ? titleId : undefined}
                                useSkipNav={false}
                            />
                        ) : (
                            <FlexWrapper {...flexWrapperStyles} itemsPerRow={brandsPerRow}>
                                {slides.map(slide => slide.renderComponent())}
                            </FlexWrapper>
                        )}
                        <span ref={afterGallery} tabIndex={-1} />
                    </DataViewLoadingOverlay>
                </StyledWrapper>
            </>
        );
        /* Valtech Custom Code - End */
    }
}

/* Valtech Custom Code - Start */
const carouselTab = { displayName: "Carousel", sortOrder: 1 };
const advancedTab = { displayName: "Advanced", sortOrder: 2 };
/* Valtech Custom Code - End */

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHookProps(BrandGallery, hookPropsInjector)), // Valtech Custom Code
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Gallery",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            /* Valtech Custom Code - Start */
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
                isVisible: x => Boolean(x.fields.title),
            },
            /* Valtech Custom Code - End */
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                horizontal: true,
                fieldType: "Contextual",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "Contextual",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "Contextual",
            },
            {
                ...brandsPerRowField.definition, // Valtech Custom Code
                displayName: "Brands per row", // Valtech Custom Code
                // Valtech Removed - editorTemplate
                defaultValue: "6",
                // Valtech Removed - options
                fieldType: "Contextual",
                // Valtech Removed - tooltip
                // Valtech Removed - tooltipPosition
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand height", // Valtech Custom Code
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip: `Forces a maximum height on the brand images to scale them down`, // Valtech Custom Code
                tooltipPosition: "left", // Valtech Custom Code
            },
            /* Valtech Custom Code - Start */
            {
                name: fields.showCarousel,
                displayName: "Show Carousel",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tab: carouselTab,
            },
            {
                name: fields.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: item => !!item.fields.showCarousel,
                tab: carouselTab,
            },
            {
                ...rowsAlignmentField.definition,
                displayName: "Rows Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.left.id,
                tooltip: `Aligns the rows horizontally, only visible when there is not enough brands to fill a row`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                ...brandsHorizontalAlignmentField.definition,
                displayName: "Brands Horizontal Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
                tooltip: `Aligns the brands horizontally, only visible when a brand is not large enough to fill its column`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            {
                ...brandsVerticalAlignmentField.definition,
                displayName: "Brands Vertical Alignment",
                fieldType: "Contextual",
                defaultValue: verticalAlignmentOptions.middle.id,
                tooltip: `Aligns the brands vertically, only visible when brands have different heights in the same row`,
                tooltipPosition: "left",
                tab: advancedTab,
            },
            /* Valtech Custom Code - End */
        ],
    },
};

export default widgetModule;
